<template>
  <!-- No `h-100` here: this panel is sized by its parent's flex column (see
       .page-section-kjp in Reports.vue). `h-100` claimed the section's whole
       content height while the Reports tab strip already occupied ~53px of
       it, which pushed this panel's scroll container below the fold and made
       the last table rows unreachable. -->
  <div class="d-flex w-100">
    <div class="kjp-sidebar border-outline-dark bg-dashboard-dark-1">
      <kjp-controls :districts="districts" @change="onChange" />
    </div>
    <div class="flex-grow-1 d-flex flex-column overflow-hidden">
      <div class="section-header w-100 bg-dashboard-dark-1 border-outline-dark-bottom d-flex align-items-center justify-content-between px-3">
        <span class="text-light small font-weight-bold">
          Yearly Capping Quantity, Year {{ params.year }}
        </span>
        <div class="d-flex align-items-center">
          <div v-if="activeModel" class="kjp-view-toggle btn-group btn-group-sm mr-2" role="group">
            <button type="button" class="btn" :class="activeView === 'data' ? 'btn-primary' : 'btn-outline-light'"
              @click="activeView = 'data'">
              Data
            </button>
            <button type="button" class="btn" :class="activeView === 'chart' ? 'btn-primary' : 'btn-outline-light'"
              @click="activeView = 'chart'">
              Chart
            </button>
          </div>
          <button class="btn btn-sm btn-primary rounded-pill px-3" :disabled="!hasAnyModel || isProcessing" @click="exportExcel">
            <font-awesome-icon :icon="['fas', 'file-download']" /> Excel
          </button>
        </div>
      </div>
      <kjp-tiles v-if="params.scopes.length" :tiles="tiles" />
      <div v-if="params.scopes.length" class="kjp-tabbar d-flex align-items-end px-3 pt-2 bg-dashboard-dark-1">
        <a v-for="(s, i) in params.scopes" :key="s" href="#" class="kjp-tab"
          :class="[{ active: activeScope === s, 'kjp-tab-error': scopeErrors[s] }, i > 0 ? 'ml-1' : '']"
          :style="scopeTabStyle(s)"
          @click.prevent="activeScope = s">
          {{ scopeLabel(s) }}
          <font-awesome-icon v-if="scopeErrors[s]" :icon="['fas', 'exclamation-triangle']" class="ml-1" />
        </a>
      </div>
      <div class="kjp-content flex-grow-1 overflow-auto p-2" :style="contentStyle">
        <kjp-chart v-if="activeModel && activeView === 'chart'" :model="activeModel" :scope="activeScope" />
        <kjp-table v-else-if="activeModel" :model="activeModel" :year="params.year"
          :start-month="params.startMonth" :month-count="params.monthCount" />
        <p v-else-if="activeScopeFailed" class="small font-italic text-center text-danger mt-4">
          Could not load KJP data for {{ activeScope }}. Click Apply to try again.
        </p>
        <p v-else-if="activeScope" class="small font-italic text-center text-muted mt-4">
          No KJP data for {{ activeScope }} with the selected districts and year.
        </p>
        <p v-else class="small font-italic text-center text-muted mt-4">
          Select options and click Apply
        </p>
      </div>
    </div>
    <spinner v-if="isProcessing" :message="loadingMessage" />
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import Swal from "sweetalert2";
import { saveAs } from "file-saver";
import API from "@/api";
import Spinner from "@/components/Spinner";
import KjpControls from "@/components/kjp/KjpControls";
import KjpTable from "@/components/kjp/KjpTable";
import KjpChart from "@/components/kjp/KjpChart";
import KjpTiles from "@/components/kjp/KjpTiles";
import { scopeLabel } from "@/components/kjp/scopes";
import { utilisationRow } from "@/components/kjp/utilisation";
import { KJP_COLORS, SCOPE_TAB_INK, scopeColor, scopeTint } from "@/components/kjp/colors";
import { KJP_TITLES } from "@/components/kjp/titles";
import { KJP_LEADING_COLUMNS, mergedLeadingColumns } from "@/components/kjp/columns";
import {
  rollupHeaders, EXPORT_WIDTHS, DERIVATION_BLOCKS, derivationRows,
} from "@/components/kjp/exportLayout";
import { districtLengths, annualQuota, quotaSplit } from "@/kjp";
import { buildChartSvg, CHART_VB } from "@/components/kjp/chartSvg";
import { buildKjpModel, round2 } from "@/kjp";

export default {
  name: "kjp-summary",
  components: { Spinner, KjpControls, KjpTable, KjpChart, KjpTiles },
  props: ["userRole", "userDistricts"],
  data() {
    return {
      // Every district ever fetched from districtsOnly, unfiltered — the
      // ground truth. `districts` (below) is a narrowed VIEW derived from
      // this and is never reassigned wholesale, only mutated in place, so a
      // district recognised as entitled after an earlier, data-sparse Apply
      // can still be added back to the view later (Fix 3).
      allDistricts: [],
      // The checkbox list KjpControls actually renders. Its array identity
      // is preserved by mutating it in place (splice/push) rather than
      // reassigning it, so KjpControls' immediate `districts` watcher
      // (which re-ticks every district whenever the prop's reference
      // changes) only fires on the initial real fetch, never again just
      // because narrowing recomputed the same or a smaller set (Fix 2) —
      // otherwise every Apply would silently re-select districts the user
      // had deliberately unchecked.
      districts: [],
      // Union, across every Apply so far this session, of district ids seen
      // in at least one bucket returned by cases/kjp-summary (Fix 3). Grows
      // only: a request for a data-sparse year can add nothing to it but
      // can never remove a district an earlier request already proved
      // entitled, which is what keeps a sparse year from shrinking the
      // sidebar, and what stops the very first Apply's result from
      // latching in a too-small list for the rest of the session.
      entitledEver: new Set(),
      // One built model per selected scope, keyed by scope — e.g.
      // { R01: {...}, R06: {...} }. A scope present here with a null value
      // means "fetched fine, nothing to show"; a scope absent from both this
      // and scopeErrors just hasn't been asked for yet.
      models: {},
      // { [scope]: true } for a scope whose fetch/build threw. Kept separate
      // from `models` so a failed scope can be told apart from an empty one
      // instead of both collapsing to "no data".
      scopeErrors: {},
      activeScope: null,
      // Shared across every scope's inner tab rather than kept per-scope —
      // switching the Data/Chart toggle stays put when the user switches
      // tabs, and switching tabs never resets it back to Data. Reads
      // whichever model is already in `models`; toggling never triggers a
      // fetch (INFS-1283 criterion 2).
      activeView: "data",
      params: { scopes: [], year: new Date().getFullYear() },
      isProcessing: false,
      loadingMessage: null,
    };
  },
  computed: {
    activeModel() {
      return this.activeScope ? this.models[this.activeScope] || null : null;
    },
    // The panel's top edge carries the active tab's colour, so the tab reads
    // as joined to the content beneath it (see .kjp-content). Empty before a
    // scope is picked: no tab is active, so there is nothing to join to.
    contentStyle() {
      const color = this.activeScope ? scopeColor(this.activeScope) : null;

      return color ? { "--tab-color": color } : {};
    },
    activeScopeFailed() {
      return !!(this.activeScope && this.scopeErrors[this.activeScope]);
    },
    hasAnyModel() {
      return Object.values(this.models).some((m) => m && m.rows.length);
    },
    // The utilisation tile row above the tabs. Every figure is selected from
    // the already-built models by utilisation.js — no arithmetic happens here,
    // and nothing is fetched: switching tabs or toggling Data/Chart never
    // recomputes these, and the contract-window controls never move them (see
    // THE PERIOD RULE in utilisation.js).
    tiles() {
      return utilisationRow({
        scopes: this.params.scopes,
        models: this.models,
        scopeErrors: this.scopeErrors,
      });
    },
  },
  methods: {
    scopeLabel,
    // The colours reach the stylesheet as custom properties so the .kjp-tab
    // rules stay plain CSS while SCOPE_COLORS (colors.js) stays the only place
    // a scope's hex is written down.
    scopeTabStyle(code) {
      const ink = SCOPE_TAB_INK[code] || { active: "#111111", dimmed: "#ffffff" };

      return {
        "--tab-color": scopeColor(code),
        "--tab-dimmed": scopeTint(code, 0.6),
        "--tab-ink": ink.active,
        "--tab-ink-dimmed": ink.dimmed,
      };
    },
    async onChange(params) {
      this.params = params;

      // Keep the tab the user is on if it's still part of the selection;
      // otherwise fall back to the first selected scope. Never blank the
      // view outright just because Apply was clicked again.
      if (!params.scopes.includes(this.activeScope)) {
        this.activeScope = params.scopes[0] || null;
      }

      this.isProcessing = true;
      this.loadingMessage = "Loading KJP data";

      const models = {};
      const scopeErrors = {};
      // Union of entitled district ids across every scope processed in this
      // Apply. Collected during the loop but only ever folded into
      // `entitledEver` once, after the loop completes — narrowing on the
      // first scope to return data latched onto R01's entitled set and
      // dropped districts that only had R06 cases before R06 was even
      // fetched.
      const entitledUnion = new Set();

      // Covers the whole operation — the refresh below included — in one
      // try/finally (Fix 5). A malformed refresh response used to throw
      // outside any try/finally here, leaving isProcessing stuck at true
      // (spinner up) until a page reload; refreshDistrictContractLengths
      // itself no longer throws for that case (see its own comment), but
      // this still guards against anything else unwinding out of the loop
      // below.
      try {
        // Contract road lengths (protocolKM/primaryKM/secondaryKM/minorKM)
        // can be populated by the backend after this page was already open —
        // getDistricts() previously ran only from mounted(), so a session
        // that outlived that backfill kept serving null km for the rest of
        // its life: the KONTRAK (KM) column read "—" and every monthly
        // ceiling silently computed 0÷n. Refresh here, on every Apply, so
        // this reference data can't go stale within a session.
        await this.refreshDistrictContractLengths();

        for (const scope of params.scopes) {
          try {
            const { buckets, unit } = await this.loadBuckets({ ...params, scope });

            buckets.forEach((b) => entitledUnion.add(b.district));

            // The endpoint already returns only districts this user is
            // entitled to, so the only client-side narrowing is the user's
            // own checkbox selection. Re-filtering against user.districts
            // would be redundant.
            const districts = this.districtsFor(buckets);

            // buildKjpModel would still return a lone "JUMLAH KESELURUHAN"
            // block of 0.00 for rows: [] — that reads as "no work done" when
            // it means "nothing to show for this selection". Leave this
            // scope's model null instead of building one.
            models[scope] = districts.length
              ? buildKjpModel({ ...params, scope, districts, buckets, unit })
              : null;
          } catch (err) {
            console.error(err);
            scopeErrors[scope] = true;
          }
        }
      } finally {
        this.models = models;
        this.scopeErrors = scopeErrors;
        this.isProcessing = false;
      }

      // Entitlement is enforced server-side: buckets only ever name districts
      // this caller can see. Fold this Apply's observations into the
      // running, monotonically-growing entitled set and re-derive the
      // sidebar's checkbox list from it every time (Fix 3) — never a
      // one-shot latch, and never removes a district a previous Apply
      // already proved entitled, so a district with zero cases in *this*
      // year/scope selection cannot be dropped forever just because this
      // particular request happened to be data-sparse.
      entitledUnion.forEach((id) => this.entitledEver.add(id));
      this.applyDistrictNarrowing();

      const failed = params.scopes.filter((s) => scopeErrors[s]);
      const empty = params.scopes.filter((s) => !scopeErrors[s] && !models[s]);

      if (failed.length) {
        Swal.fire(
          "Could not load KJP data",
          `${failed.join(", ")} could not be fetched. ` +
            `${failed.length === params.scopes.length
              ? "Please try again."
              : "Those tabs are marked unavailable rather than left blank."}`,
          "error"
        );
      } else if (params.scopes.length && empty.length === params.scopes.length) {
        Swal.fire(
          "No data",
          "There is no KJP data for the selected districts, year and scope.",
          "info"
        );
      }
    },
    async loadBuckets({ scope, year }) {
      const { data } = await API.get(`cases/kjp-summary?year=${year}&reportType=${scope}`);

      return { buckets: data.buckets || [], unit: data.unit };
    },
    async getDistricts() {
      this.isProcessing = true;

      try {
        // Fetched for contract lengths only. Entitlement is enforced by the
        // kjp-summary endpoint, so this list is not the access boundary —
        // districts absent from every scope's bucket payload are narrowed
        // out of the view in onChange (see applyDistrictNarrowing).
        const { data } = await API.get("districtsOnly?_sort=name");
        this.allDistricts = data;
        // A distinct array from `allDistricts`, even though it starts out
        // holding the exact same district objects — later narrowing splices
        // this one in place (Fix 2/3) and must never touch the ground-truth
        // array it's re-derived from.
        this.districts = data.slice();
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Could not load districts",
          "The district list could not be fetched. Please refresh and try again.",
          "error"
        );
      } finally {
        this.isProcessing = false;
      }
    },
    // Re-fetches district records and merges fresh contract-km fields onto
    // the ones already in `this.allDistricts`, in place — called once per
    // Apply (INFS-1281) so protocolKM/primaryKM/secondaryKM/minorKM cannot
    // go stale for the rest of a session the way getDistricts()-at-mount-
    // only did.
    //
    // Reads/writes `allDistricts` (the ground truth), not the narrowed
    // `districts` view — a district currently hidden by narrowing still
    // deserves fresh contract-km data, since it can be shown again the
    // moment a later Apply proves it entitled (Fix 3).
    async refreshDistrictContractLengths() {
      let fresh;

      try {
        const { data } = await API.get("districtsOnly?_sort=name");

        // Fix 5: validate the shape before anything downstream maps or
        // iterates it. A malformed 200 (body not an array) used to throw
        // here uncaught — .map/.forEach on a non-array — which unwound out
        // of onChange with isProcessing still true and no finally left to
        // catch it, wedging the spinner until a page reload. Treating it as
        // just another fetch failure reuses the already-correct fallback
        // below: keep serving stale-but-real figures rather than crash.
        if (!Array.isArray(data)) {
          throw new Error(`districtsOnly returned ${typeof data}, not an array`);
        }

        fresh = data;
      } catch (err) {
        console.error(err);
        // Keep serving whatever was already loaded (from mount, or an
        // earlier successful Apply) rather than let a failed refresh here
        // wipe it out. Stale-but-real contract figures beat silently
        // proceeding with an emptied district list, which would make every
        // contractKm compute as the falsy "no data" case and read as a
        // fabricated zero rather than the honest "not known".
        Swal.fire(
          "Could not refresh districts",
          "Showing previously loaded contract figures, which may be out of date.",
          "warning"
        );

        return;
      }

      const byId = new Map(fresh.map((d) => [d._id, d]));
      // The only fields this feature ever reads off a district record
      // (districtLengths in kjp.js). Reset before merging, not left for
      // Object.assign to overwrite — Object.assign only ever copies keys
      // `updated` actually carries, so a field the backend cleared (key
      // dropped rather than sent as an explicit null) would otherwise
      // survive the merge untouched, and every quota derived from it would
      // silently keep computing from data that no longer exists (Fix 4).
      const KM_FIELDS = ["protocolKM", "primaryKM", "secondaryKM", "minorKM"];

      this.allDistricts.forEach((d) => {
        const updated = byId.get(d._id);

        KM_FIELDS.forEach((f) => {
          d[f] = null;
        });

        if (updated) {
          Object.assign(d, updated);
        } else {
          // Gone from the backend entirely (deleted, or this session's
          // access to it withdrawn) — leaving its last-known contract
          // length in place would silently show quotas computed from data
          // that no longer exists. Clearing it instead makes
          // buildKjpModel's own NO_CONTRACT_LENGTH warning fire for it
          // exactly as it already does for a district that was never given
          // a contract length: an honest description of what's actually
          // known about it now, surfaced through the same banner rather
          // than served as if nothing had changed.
          console.error(`KJP: district ${d.name} (${d._id}) missing from districtsOnly refresh`);
        }
      });
    },
    // Recomputes which districts the sidebar should show (Fix 3) — the
    // union of every district id seen entitled so far this session
    // (`entitledEver`), or the full list when nothing has been observed
    // entitled yet (before the very first Apply that returns any buckets).
    // Re-derived from `allDistricts` (never from whatever `districts`
    // already holds) so a district narrowed out by an earlier, data-sparse
    // Apply is still reachable once entitledEver grows to include it.
    applyDistrictNarrowing() {
      const target = this.entitledEver.size
        ? this.allDistricts.filter((d) => this.entitledEver.has(d._id))
        : this.allDistricts;

      this.mergeDistrictsInPlace(target);
    },
    // Reconciles `this.districts` (the array bound to KjpControls) to hold
    // exactly `target`'s districts, without ever replacing the array
    // reference itself (Fix 2). KjpControls' `districts` watcher is
    // `immediate: true` and re-ticks every district whenever that prop's
    // identity changes — a wholesale reassignment here would silently
    // re-select districts the user had deliberately unchecked, on every
    // single Apply. splice/push mutate Vue's already-reactive array in
    // place, so KjpControls still re-renders correctly without the prop's
    // reference ever needing to change.
    mergeDistrictsInPlace(target) {
      const targetIds = new Set(target.map((d) => d._id));

      for (let i = this.districts.length - 1; i >= 0; i -= 1) {
        if (!targetIds.has(this.districts[i]._id)) this.districts.splice(i, 1);
      }

      const presentIds = new Set(this.districts.map((d) => d._id));
      target.forEach((d) => {
        if (!presentIds.has(d._id)) this.districts.push(d);
      });
    },
    // Districts the endpoint returned for this caller, narrowed to the user's
    // checkbox selection. Entitlement is enforced server-side.
    districtsFor(buckets) {
      const entitled = new Set(buckets.map((b) => b.district));

      return this.districts.filter(
        (d) => entitled.has(d._id) && this.params.districtIds.includes(d._id)
      );
    },
    // ExcelJS solid-fill shorthand. Colours come from colors.js only, so the
    // screen (KjpTable.vue) and this export can never disagree on the
    // workbook palette.
    fill(hex) {
      return { type: "pattern", pattern: "solid", fgColor: { argb: `FF${hex}` } };
    },
    async exportExcel() {
      // Fix 2 (INFS-1281): re-entrancy guard. The button below disables
      // itself once isProcessing flips true, but that's a render away —
      // a second click landing before Vue re-renders (or any other path
      // that could call this twice) must still be a no-op, not kick off a
      // second concurrent workbook build and a second download. What used
      // to be one fast writeBuffer() is now three browser image decodes
      // plus three base64 encodes of large canvases (one per scope's
      // chart), so that window is no longer negligible.
      if (this.isProcessing) return;

      this.isProcessing = true;
      this.loadingMessage = "Generating Excel file";

      // Same treatment onChange gives its own async work: one try/finally
      // covering the whole operation, so isProcessing always comes back
      // down, plus a catch that surfaces failure through Swal instead of
      // an unhandled rejection that makes the click silently appear to do
      // nothing.
      try {
        const wb = new ExcelJS.Workbook();
        wb.created = new Date();

        const p = this.params;
        const lastMonth = p.startMonth + p.monthCount - 1;

        // asAtLabel is scope-independent — every model was built from the
        // same asAtMonth — so any one built model can supply it for a scope
        // whose own model is missing (failed fetch or empty result). The
        // Excel button stays disabled while hasAnyModel is false, so a
        // model is guaranteed to exist here.
        const anyModel = Object.values(this.models).find((m) => m);

        // One worksheet per selected scope — separate tabs in the same file,
        // named R01/R02/R06 — rather than one sheet with every scope written
        // as a sequential section. Reads the models already built for the
        // screen; a scope on screen is never refetched here.
        //
        // Sequential, not Promise.all: writeScopeSheet rasterises this scope's
        // chart (an async canvas round-trip) before returning, and every one
        // of those image loads must resolve before wb.xlsx.writeBuffer() runs
        // below — a race here would silently ship a workbook with an image
        // missing from whichever sheet's canvas hadn't finished yet.
        for (const scope of p.scopes) {
          const ws = wb.addWorksheet(this.exportSheetName(scope, p));

          await this.writeScopeSheet(wb, ws, scope, this.models[scope], this.scopeErrors[scope], p, lastMonth, anyModel);

          // Column widths taken from each source sheet's own <cols> block
          // (EXPORT_WIDTHS), rather than the three-value guess this used to
          // apply. The workbook does not use one width everywhere: MAKLUMAT is
          // the widest column on every sheet, the month columns are uniform,
          // and the four roll-ups are wide enough that their two-line headers
          // wrap to a sensible shape instead of spilling.
          const w = EXPORT_WIDTHS[scope] || EXPORT_WIDTHS.R01;
          const leadN = (KJP_LEADING_COLUMNS[scope] || KJP_LEADING_COLUMNS.R01).length;
          const block = DERIVATION_BLOCKS[scope] || DERIVATION_BLOCKS.R01;

          ws.columns.forEach((col, i) => {
            if (i < leadN) col.width = w.lead[i] || 14;
            else if (i < leadN + 12) col.width = w.month;
            else if (i < leadN + 16) col.width = w.rollup;
            // One spacer column, then the derivation block's own widths.
            else col.width = block.widths[i - (leadN + 17)] || 4;
          });
        }

        const buf = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buf]), this.exportFileName(p, lastMonth, anyModel));
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Could not generate Excel file",
          "The export could not be completed. Please try again.",
          "error"
        );
      } finally {
        this.isProcessing = false;
        this.loadingMessage = null;
      }
    },
    // Contract-Ceiling_<year>_<startAbbr>-<endAbbr>_as-at-<asAtAbbr>_<scopes>_<yyyymmdd>.xlsx,
    // e.g. Contract-Ceiling_2026_Jan-Dec_as-at-Aug_R01-R02-R06_20260819.xlsx (INFS-1281).
    // The prefix was KJP-Summary_ until Infrasel renamed the tab (INFS-1454).
    // Replaces raw month numbers (`1-12 as at 8`) with the same three-letter
    // abbreviations the table and sheet headers already use, so the filename
    // reads the same way the workbook does.
    //
    // Month abbreviations are read off `anyModel.months` (built by
    // calendarMonths in kjp.js) rather than a second hardcoded list — that
    // array is scope-independent and always the full twelve calendar months,
    // so `[monthNumber - 1]` always lands on the right one regardless of the
    // contract window. `anyModel` is guaranteed non-null: see its own comment
    // in exportExcel.
    // Sheet names follow the source workbook's own convention, "Data% - <scope>_<n>m"
    // (e.g. "Data% - R01_12m"), rather than the bare scope code this used to
    // write. The workbook keeps one sheet per scope per contract length — its
    // _12m and _10m variants are the same table with a different divisor — and
    // that is exactly what the Months control selects here, so the contract
    // length belongs in the name for a reader comparing the two side by side.
    //
    // Excel caps a sheet name at 31 characters and forbids : \ / ? * [ ];
    // nothing this builds can reach either limit ("Data% - R01_12m" is 15 and
    // uses no reserved character), so no sanitising is needed.
    exportSheetName(scope, p) {
      return `Data% - ${scope}_${p.monthCount}m`;
    },
    exportFileName(p, lastMonth, anyModel) {
      const abbr = (monthNumber) => anyModel.months[monthNumber - 1].label.slice(0, 3);
      const pad2 = (n) => String(n).padStart(2, "0");
      const now = new Date();
      const yyyymmdd = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`;

      return `Contract-Ceiling_${p.year}_${abbr(p.startMonth)}-${abbr(lastMonth)}` +
        `_as-at-${abbr(p.asAtMonth)}_${p.scopes.join("-")}_${yyyymmdd}.xlsx`;
    },
    // One scope's whole sheet: title, header block, table (or its
    // "unavailable" stand-in), JUMLAH KESELURUHAN block, the three
    // projection rows and — new for INFS-1283 — that scope's chart, embedded
    // as a rasterised image below all of the above. Every row number used for
    // merges and percent formatting is read back off `ws` (first/ws.rowCount/
    // lastRow) rather than tracked by hand, so it is always the row's real
    // position on this scope's own sheet — simpler than when every scope
    // shared one sheet, since ws.rowCount now starts fresh per scope instead
    // of continuing across sections.
    //
    // async because embedding the chart is an async canvas round-trip
    // (renderChartPng); the caller (exportExcel) awaits this per scope before
    // moving to the next one, so every image has finished loading before
    // wb.xlsx.writeBuffer() ever runs.
    async writeScopeSheet(wb, ws, scope, model, hasError, p, lastMonth, anyModel) {
      // The workbook hides gridlines on every data sheet — with the table's own
      // borders drawn, Excel's grey grid behind them is visual noise.
      ws.views = [{ showGridLines: false }];

      ws.addRow([KJP_TITLES[scope]]);
      ws.lastRow.font = { bold: true, underline: true, size: 16 };

      // model may be null (failed fetch or empty result) — asAtLabel is
      // scope-independent, so anyModel (guaranteed to exist, see
      // exportExcel) always has it even when this sheet's own model doesn't.
      const asAtLabel = (model || anyModel).asAtLabel;

      // Same wording as the on-screen heading (INFS-1454), so the exported
      // file and the page a reader compared it against agree.
      ws.addRow([`Yearly Capping Quantity, Year ${p.year} · contract months ${p.startMonth}-${lastMonth} · as at ${asAtLabel}`]);
      ws.lastRow.font = { bold: true };
      ws.addRow([`Generated ${new Date().toISOString().slice(0, 16).replace("T", " ")}`]);
      ws.addRow([]);

      // A model with no rows carries no more information than a failed
      // fetch — both would otherwise write a sheet of zeros that reads as
      // "no work done". Mark the two cases with different wording so a
      // failed fetch isn't silently indistinguishable from a genuinely
      // empty selection.
      if (!model || !model.rows.length) {
        ws.addRow([hasError
          ? "Data could not be loaded for this scope."
          : "No data for the selected districts, year and scope."]);
        ws.addRow(["No figures are shown rather than zeros, which would read as no work done."]);

        return;
      }

      // Absolute row numbers on this sheet (ws.rowCount counts from the top
      // of this scope's own sheet now, not across a shared one).
      const pctRowNumbers = [];
      const monthCount = model.months.length;

      // Leading-column layout differs per scope (Defect C) — read from
      // columns.js, the same config KjpTable.vue renders from, rather than
      // hardcoding R01's four columns for every sheet. `leadCount` is what
      // every column-position calculation below (merges, hidden months,
      // percent formatting) is offset by, instead of a bare literal.
      const leadCols = KJP_LEADING_COLUMNS[scope] || KJP_LEADING_COLUMNS.R01;
      const mergedCols = mergedLeadingColumns(scope)
        .map((c) => ({ ...c, col: leadCols.indexOf(c) + 1 }));
      const leadCount = leadCols.length;

      // The workbook's own full wording, unit on a second line — not the
      // abbreviated "JUMLAH SEHINGGA <MONTH>" this used to write, which is
      // where the exported file stopped resembling the sheet it mirrors.
      // See rollupHeaders in exportLayout.js.
      ws.addRow([
        ...leadCols.map((c) => c.label),
        ...model.months.map((m) => m.label),
        ...rollupHeaders(scope, model.asAtLabel),
      ]);
      this.styleHeaderRow(ws.lastRow, monthCount, leadCount);

      // round2, not toFixed — see the note on round2 in src/kjp.js. Writing a
      // raw float here would put 5034.224999999999 in the cell.
      const val = (sub, c) => {
        if (c.unmeasured) return "—";
        if (sub.kind === "pct") return Math.round(c.value) / 100;

        return round2(c.value);
      };
      // Null first: on quota/class/count sub-rows the BAKI columns are
      // structurally absent (only ceiling rows carry a baki at all), so an
      // unmeasured district must still render blank there, not "—" — a dash
      // in a column that never has a baki reads as a missing measurement
      // that was never expected to exist.
      const roll = (sub, v, unmeasured) =>
        v == null ? "" : unmeasured ? "—" : sub.kind === "pct" ? Math.round(v) / 100 : round2(v);

      // Same precision rule as the on-screen table's quotaCell: a 'class'
      // row's quota is a contract length (Defect B), written at 3dp like
      // KONTRAK (KM) elsewhere — e.g. Gombak's 64.605, not a round2'd 64.61 —
      // while every other quota (ceiling, R01's split types) stays at 2dp.
      const quotaValue = (sub) => {
        if (sub.quota == null) return "";

        return sub.kind === "class" ? Number(sub.quota.toFixed(3)) : round2(sub.quota);
      };

      // The leading cells for one district's sub-row, in this scope's own
      // column order — 'district'/'contractTotal' only carry a value on the
      // first sub-row (blank below it, then merged down by the caller);
      // 'label' and 'quota' repeat every sub-row.
      const leadingRowValues = (row, sub, i) => leadCols.map((c) => {
        if (c.type === "district") return i === 0 ? row.districtName : "";
        if (c.type === "contractTotal") {
          return i === 0 ? (row.contractKm ? Number(row.contractKm.toFixed(3)) : "—") : "";
        }
        if (c.type === "label") return sub.label;

        return quotaValue(sub); // 'quota'
      });

      // The derivation block sits to the right of the roll-up columns, one
      // spacer column clear of them, exactly as the workbook lays it out.
      // Written per district as that district's rows are written, so the
      // block's rows line up with the district block they explain.
      const blockStart = leadCount + monthCount + 4 + 2;
      const block = DERIVATION_BLOCKS[scope] || DERIVATION_BLOCKS.R01;
      const writeBlock = (districtRow, firstRowNumber) => {
        // buildKjpModel's rows carry the district's id and name but not its
        // raw contract lengths, so the block reads them back from the fetched
        // district records — the same objects buildKjpModel itself was given,
        // so the block cannot derive its figures from different lengths than
        // the table beside it.
        const source = this.allDistricts.find((d) => d._id === districtRow.districtId);
        if (!source) return;

        const lengths = districtLengths(source);
        const annual = annualQuota(scope, lengths);
        const rows = derivationRows({
          scope, lengths, annual, monthCount: p.monthCount, split: quotaSplit(scope, annual),
        });

        rows.forEach((values, i) => {
          values.forEach((v, j) => {
            const cell = ws.getCell(firstRowNumber + i, blockStart + j);
            cell.value = typeof v === "number" ? round2(v) : v;
            cell.border = this.thinBorder();
            cell.alignment = j === 0
              ? { vertical: "middle" }
              : { horizontal: "right", vertical: "middle" };
          });
        });
      };

      // Block header: a merged banner one row above the table's own header
      // row, with the block's column names sitting on that header row, so the
      // two line up the way the workbook has them.
      const headerRowNumber = ws.rowCount;
      const bannerRow = headerRowNumber - 1;

      ws.getCell(bannerRow, blockStart).value = block.banner;
      ws.mergeCells(bannerRow, blockStart, bannerRow, blockStart + block.columns.length);
      ws.getCell(bannerRow, blockStart).font = { bold: true };
      ws.getCell(bannerRow, blockStart).alignment = { horizontal: "center" };
      ["", ...block.columns].forEach((label, j) => {
        const cell = ws.getCell(headerRowNumber, blockStart + j);
        cell.value = label;
        cell.font = { bold: true };
        cell.fill = this.fill(KJP_COLORS.headerFill);
        cell.alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
        cell.border = this.thinBorder();
      });

      model.rows.forEach((row) => {
        const first = ws.rowCount + 1;

        row.subRows.forEach((sub, i) => {
          if (sub.kind === "pct") pctRowNumbers.push(ws.rowCount + 1);
          ws.addRow([
            ...leadingRowValues(row, sub, i),
            ...sub.cells.map((c) => val(sub, c)),
            roll(sub, sub.usedToDate, sub.unmeasuredToDate),
            roll(sub, sub.bakiToDate, sub.unmeasuredToDate),
            roll(sub, sub.usedAnnual, sub.unmeasuredAnnual),
            roll(sub, sub.bakiAnnual, sub.unmeasuredAnnual),
          ]);

          this.styleDataRow(ws.lastRow, monthCount, sub.kind === "ceiling" || sub.kind === "pct", false, leadCount);
        });

        mergedCols.forEach((c) => ws.mergeCells(first, c.col, ws.rowCount, c.col));
        ws.getCell(first, mergedCols[0].col).font = { bold: true };

        // AFTER the district's own rows, never before: ws.getCell() on a row
        // past the current end extends the sheet's row count, so writing the
        // block first pushed every following addRow() below it — the district
        // merges came out spanning seven rows instead of four and the block
        // drifted further from its district with each one. Writing it here,
        // against the `first` captured above, keeps the two aligned.
        writeBlock(row, first);
      });

      // Only the first merged column (district, always leftmost per
      // columns.js) carries "JUMLAH KESELURUHAN"; any other merged column
      // (R01's contract-length total) stays blank so the single merged cell
      // below doesn't duplicate the text into a second column.
      const totalStart = ws.rowCount + 1;
      model.totals.forEach((sub, i) => {
        if (sub.kind === "pct") pctRowNumbers.push(ws.rowCount + 1);
        ws.addRow([
          ...leadCols.map((c, idx) => {
            if (c.type === "district" || c.type === "contractTotal") {
              return idx + 1 === mergedCols[0].col && i === 0 ? "JUMLAH KESELURUHAN" : "";
            }
            if (c.type === "label") return sub.label;

            return quotaValue(sub); // 'quota'
          }),
          ...sub.cells.map((c) => val(sub, c)),
          roll(sub, sub.usedToDate, sub.unmeasuredToDate),
          roll(sub, sub.bakiToDate, sub.unmeasuredToDate),
          roll(sub, sub.usedAnnual, sub.unmeasuredAnnual),
          roll(sub, sub.bakiAnnual, sub.unmeasuredAnnual),
        ]);

        this.styleDataRow(ws.lastRow, monthCount, true, true, leadCount);
      });
      // Merged leading columns are always contiguous from column 1 (district
      // first, then, for R01 only, the contract-length total) — mergedCols.
      // length is therefore the exact width of the single "JUMLAH
      // KESELURUHAN" cell, whether that's one column (R02/R06) or two (R01).
      ws.mergeCells(totalStart, 1, ws.rowCount, mergedCols.length);

      ws.addRow([]);
      const pr = model.projections;

      // The leading cells for a projection row: blank except the MAKLUMAT
      // (label) column, wherever this scope's layout puts it — R01 has it
      // third, R02/R06 have it second, so a hardcoded ["", "", label, ""]
      // prefix would misalign every month column on R02/R06's three-column
      // layout.
      const projectionLeading = (label) => leadCols.map((c) => (c.type === "label" ? label : ""));

      // The three projection rows carry the same D9D9D9 fill as the JUMLAH
      // KESELURUHAN rows in the source workbook, confirmed against the
      // sheet's own month-cell fills — so they're styled with the same
      // styleDataRow(..., true, true) call as the totals block above.
      ws.addRow([...projectionLeading("Unjuran Siling Tahunan"), ...pr.annualCeiling.map(round2)]);
      this.styleDataRow(ws.lastRow, monthCount, true, true, leadCount);
      ws.addRow([
        ...projectionLeading("Unjuran Penggunaan Siling"),
        ...pr.cumulativeUsage.map((v, i) => (pr.cumulativeUsageUnmeasured[i] ? "—" : round2(v))),
      ]);
      this.styleDataRow(ws.lastRow, monthCount, true, true, leadCount);
      ws.addRow([...projectionLeading("Peruntukan Siling Bulanan"), ...pr.monthlyAllocation.map(round2)]);
      this.styleDataRow(ws.lastRow, monthCount, true, true, leadCount);

      // Percentages are written as fractions so Excel treats them as numbers;
      // without the format they would display as 0.45 where the screen shows 45%.
      pctRowNumbers.forEach((rowNumber) => {
        const row = ws.getRow(rowNumber);

        row.eachCell((cell, col) => {
          if (col > leadCount && typeof cell.value === "number") cell.numFmt = "0%";
        });
      });

      // Months outside the contract window stay in the sheet with their real
      // figures — the annual roll-up columns need them (INFS-1281, same
      // reason the model always carries all twelve: see calendarMonths in
      // kjp.js) — but are hidden by default so the contract's own window is
      // what a reader sees on open. `hidden` is purely a display attribute in
      // xlsx: values, formulas and totals are untouched, and the skipped
      // column letters make the hidden data discoverable to anyone who wants
      // to unhide. Month columns start right after this scope's own leading
      // columns (leadCount of them).
      model.months.forEach((m, i) => {
        if (m.index < p.startMonth || m.index > lastMonth) {
          ws.getColumn(leadCount + 1 + i).hidden = true;
        }
      });

      // The chart, below all of the above. Reaching this line already proves
      // `model` is real and non-empty — the early return above is the only
      // exit for a failed fetch or an empty result, so a chart is never
      // built from a model with nothing in it. buildChartSvg is the exact
      // function KjpChart.vue renders on screen (see chartSvg.js); rendering
      // failures (canvas/Image are browser-only) are caught so a sheet still
      // keeps every figure the table has even if its picture couldn't be
      // rasterised.
      ws.addRow([]);
      try {
        const { base64, width, height } = await this.renderChartPng(model, scope);
        const imageId = wb.addImage({ base64, extension: "png" });

        ws.addImage(imageId, {
          tl: { col: 0, row: ws.rowCount },
          ext: { width, height },
        });
      } catch (err) {
        // Fix 1 (INFS-1283): a decode/SecurityError failure here used to be
        // silent — console.error only — leaving a sheet where every figure
        // is present and the chart has simply vanished, indistinguishable
        // from a build where no chart was ever expected. Write an
        // explanatory row instead, the same spirit as the "unavailable" rows
        // above for a missing model: the reader is told the table above is
        // complete and only the picture is missing, rather than left to
        // guess. Also the path a Fix 3 timeout surfaces through — it rejects
        // the same renderChartPng promise awaited above.
        console.error(err);
        ws.addRow(["Chart image could not be generated for this scope."]);
        ws.addRow(["The figures above are complete; only the chart image is missing."]);
      }
    },
    // Rasterises this scope's chart to a PNG data URL, at 2x the SVG's own
    // pixel size for print quality (a 1x raster looks soft once Excel or a
    // printer scales it up). SVG string -> Blob -> object URL -> Image -> a
    // 2x <canvas> -> data URL, with the object URL always revoked afterwards
    // regardless of outcome. `width`/`height` returned are the SVG's own
    // (1x) footprint — ext controls the image's placed size on the sheet,
    // never its raster density, so it stays the same size the on-screen
    // chart renders at even though the PNG behind it is sharper.
    async renderChartPng(model, scope) {
      const svg = buildChartSvg(model, scope);
      const scale = 2;
      const blob = new Blob([svg], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);

      try {
        const img = await new Promise((resolve, reject) => {
          const image = new Image();

          // Fix 3 (INFS-1283): onload/onerror used to be this promise's only
          // two exits. A decode that stalls without ever calling either one
          // (as opposed to erroring outright) left this — and exportExcel,
          // which awaits writeScopeSheet sequentially per scope — unsettled
          // forever, with no busy state before Fix 2 to even hint the click
          // had done anything. 15s is generous for a large canvas decode on
          // a slow machine (this is a same-origin blob: URL, not a network
          // fetch, so a healthy decode finishes in well under a second) but
          // still bounded. Cleared in both onload and onerror so a slow-but-
          // eventually-successful decode can't have the timer fire after the
          // promise already settled. The rejection is caught by Fix 1's
          // try/catch around the call site, same as any other rasterisation
          // failure — it surfaces as the sheet's "chart unavailable" rows,
          // never as a silently unsettled promise.
          const timeoutMs = 15000;
          const timer = setTimeout(() => {
            reject(new Error(`Timed out rasterising ${scope} chart after ${timeoutMs}ms`));
          }, timeoutMs);

          image.onload = () => {
            clearTimeout(timer);
            resolve(image);
          };
          image.onerror = () => {
            clearTimeout(timer);
            reject(new Error(`Failed to rasterise ${scope} chart`));
          };
          image.src = url;
        });

        const canvas = document.createElement("canvas");
        canvas.width = CHART_VB.width * scale;
        canvas.height = CHART_VB.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

        return { base64: canvas.toDataURL("image/png"), width: CHART_VB.width, height: CHART_VB.height };
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    // Header row fill: the workbook's grey on every column except the two
    // "baki" (remaining quota) columns, which get its distinct highlight.
    // `leadCount` is this scope's own leading-column count (Defect C) — R01's
    // four columns is not a universal offset, R02/R06 have three.
    styleHeaderRow(row, monthCount, leadCount) {
      const bakiCols = [leadCount + monthCount + 2, leadCount + monthCount + 4];

      row.eachCell((cell, col) => {
        cell.fill = this.fill(bakiCols.includes(col) ? KJP_COLORS.headerBakiFill : KJP_COLORS.headerFill);
        cell.font = { bold: true, color: { argb: `FF${KJP_COLORS.text}` } };
        // Wrapped and centred, like the workbook. Without wrapText the four
        // roll-up headers are single long lines that either spill across their
        // neighbours or force absurd column widths — and their unit, which the
        // workbook puts on a second line, would not show as a second line at
        // all. `vertical: middle` keeps the short headers (DAERAH, month
        // labels) centred against the tall wrapped ones beside them.
        cell.alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
        cell.border = this.thinBorder();
      });

      // Tall enough for the longest roll-up header to wrap to ~4 lines at the
      // widths set in EXPORT_WIDTHS. Left to Excel's autofit, a wrapped header
      // row renders at one line's height with the rest clipped.
      row.height = 58;
    },
    // The workbook rules every cell of the table. Kept in one place so the
    // header, the data rows and the derivation block cannot drift apart.
    thinBorder() {
      const side = { style: "thin", color: { argb: "FF808080" } };

      return { top: side, left: side, bottom: side, right: side };
    },
    // Month columns only — the roll-up columns (to date / annual / baki)
    // stay unfilled with default (black) text, same rule as the on-screen
    // table. `isTotal` picks the JUMLAH KESELURUHAN shade over the district
    // rows' shade; `bold` carries the ceiling/percentage/totals emphasis.
    // `leadCount` — see styleHeaderRow above.
    styleDataRow(row, monthCount, bold, isTotal, leadCount) {
      const monthStart = leadCount + 1;
      const monthEnd = leadCount + monthCount;
      const fillHex = isTotal ? KJP_COLORS.totalMonthFill : KJP_COLORS.monthFill;

      // includeEmpty: true — the intent here is to style the whole row, but
      // ExcelJS's default eachCell skips any cell whose value is "" (the
      // KUOTA BULANAN column on count/percentage rows, whose quota is
      // structurally null), so bold was silently never applied there.
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        if (col >= monthStart && col <= monthEnd) {
          cell.fill = this.fill(fillHex);
          cell.font = { bold, color: { argb: `FF${KJP_COLORS.text}` } };
        } else if (bold) {
          cell.font = { bold: true };
        }

        cell.border = this.thinBorder();
        // Figures right, labels left — the workbook's own alignment, and what
        // makes a column of numbers readable down its decimal point. The
        // leading columns hold the district and MAKLUMAT text.
        cell.alignment = col <= leadCount
          ? { vertical: "middle", wrapText: true }
          : { horizontal: "right", vertical: "middle" };
      });
    },
  },
  mounted() {
    this.getDistricts();
  },
};
</script>

<style lang="scss" scoped>
.kjp-sidebar {
  flex: 0 0 300px;
  max-width: 300px;
  overflow-y: auto;
}

// Folder-index tabs (INFS-1454, Hanif feedback 16 Oct): each scope's tab is a
// filled block in its own colour (SCOPE_COLORS, colors.js) sticking out of the
// panel like a ring-binder divider. Inactive tabs are the same colour dimmed;
// the active one is full colour and overlaps the panel's top edge (.kjp-content
// below, same colour) so the two read as one piece. Label colours come from
// SCOPE_TAB_INK, chosen per fill for contrast. The page-level strip in
// Reports.vue has the same shape in one accent colour (INFS-1570); styles are
// scoped per-component, so the two .kjp-tab rules never reach each other.
.kjp-tabbar {
  // Above .kjp-content so the active tab can overlap its top border.
  position: relative;
  z-index: 1;
}

.kjp-tab {
  position: relative;
  color: var(--tab-ink-dimmed);
  background-color: var(--tab-dimmed);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.3rem 1rem;
  border-radius: 8px 8px 0 0;
  // Every tab the same width whatever its label (Hanif, 19 Oct): one shared
  // basis wide enough for "R06 — Road Line Marking", shrinking equally on a
  // narrow pane rather than letting the longest label set its own size.
  flex: 0 1 15rem;
  min-width: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  // Inactive tabs sit a little lower than the active one; margin rather than
  // transform so the strip's height does not change when the tab changes.
  margin-top: 0.2rem;

  &:hover {
    color: var(--tab-ink);
    background-color: var(--tab-color);
    text-decoration: none;
  }

  &.active {
    color: var(--tab-ink);
    background-color: var(--tab-color);
    margin-top: 0;
    padding-top: 0.5rem;
    // Overlap the 3px border of .kjp-content so there is no seam.
    margin-bottom: -3px;
    padding-bottom: calc(0.3rem + 3px);
  }

  // A scope whose fetch failed must stay visibly distinct in the strip
  // rather than reading identically to a scope with no data. The fill now
  // belongs to the scope, so the error cue is an inset outline plus the
  // warning icon, not a recolour.
  &.kjp-tab-error {
    box-shadow: inset 0 0 0 2px currentColor;
  }
}

// The panel under the tabs: a 3px top edge in the active scope's colour. The
// active tab overlaps it, which is what makes tab and panel look like one
// piece of card.
.kjp-content {
  border-top: 3px solid var(--tab-color, transparent);
}
</style>
